import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { usePermission } from '../../../../permissions';

import { NavItem } from './navItems';

/** The section icon's own width plus the gap after it. Nested items start from
 *  this inset so they align with the section label above them. */
const NAV_ICON_INSET = 23;

interface SidebarMenuItemProps {
    item: NavItem;
    parentPath: string | null;
    depth: number;
    isExpanded: (itemPath: string, parentPath: string | null) => boolean;
    onToggle: (itemPath: string, parentPath: string | null) => void;
    currentPath: string;
    isFirst?: boolean;
    children?: React.ReactNode;
}

const SidebarMenuItem = memo((props: SidebarMenuItemProps) => {
    const { item, parentPath, depth, isExpanded, onToggle, currentPath, isFirst, children } = props;
    const hasPermission = usePermission((item.permissionId as string) || '');
    const noPermission = item.permissionId ? !hasPermission : false;
    const hasChildren = !!item.content && item.content.length > 0;
    const isEmptyCategory = !!item.content && item.content.length === 0;
    const expanded = hasChildren && isExpanded(item.path, parentPath);
    const isActive = !hasChildren && !isEmptyCategory && currentPath === item.path;
    const isRoot = depth === 0 && (hasChildren || isEmptyCategory);
    const isLeafLink = !hasChildren && !isEmptyCategory && !noPermission && !!item.path;

    if (item.notDeveloped) return null;

    const handleClick = (e: React.MouseEvent) => {
        if (noPermission || isEmptyCategory) {
            e.preventDefault();
            return;
        }
        if (hasChildren) {
            e.preventDefault();
            onToggle(item.path, parentPath);
        }
    };

    // Build ListItem sx. Geometry comes from the nav tokens so the Appearance
    // page can tune the rail live; the literals are fallbacks.
    const listItemSx: Record<string, unknown> = {
        margin: '1px 0',
        padding: 'var(--nav-item-py, 7px) var(--nav-item-px, 12px)',
        // Everything below a section starts from the same inset — the width of
        // the section's icon plus its gap — so a child lines up under its
        // parent's LABEL, not under the icon. Below that it is one indent step
        // per level, unchanged all the way down: the step is what tells the
        // levels apart, so it has to be the same everywhere.
        paddingLeft:
            depth === 0
                ? 'var(--nav-item-px, 12px)'
                : `calc(var(--nav-item-px, 12px) + ${NAV_ICON_INSET}px + ${
                      depth - 1
                  } * var(--nav-indent-step, 14px))`,
        borderRadius: isActive
            ? '0 var(--radius-sm, 6px) var(--radius-sm, 6px) 0'
            : 'var(--radius-sm, 6px)',
        // 2px rather than 3px, on both states so the label never shifts.
        borderLeft: '2px solid',
        borderLeftColor: isActive ? 'accent.main' : 'transparent',
        backgroundColor: isActive ? 'var(--nav-active-bg)' : 'transparent',
        transition: 'background-color 0.2s',
        color: 'inherit',
        textDecoration: 'none',
        '&:hover': {
            backgroundColor: isActive ? 'var(--nav-active-bg)' : 'var(--nav-hover-bg)',
            textDecoration: 'none',
        },
    };

    if (isRoot) {
        // Whitespace separates sections now — the horizontal rules were the
        // loudest thing on a rail whose actual links are quieter than its
        // headings. Dropping them and the 24px gaps reclaims ~110px.
        listItemSx.marginTop = isFirst ? '2px' : 'var(--nav-section-gap, 14px)';
        listItemSx.paddingTop = isFirst ? 0 : '8px';
        listItemSx.borderRadius = 0;
        listItemSx.borderLeft = '2px solid transparent';
        listItemSx.backgroundColor = 'transparent';
    }

    if (noPermission || isEmptyCategory) {
        listItemSx.pointerEvents = 'none';
        listItemSx.color = 'grey.500';
    }

    // Label styles
    // No fontFamily override: 'Varela' ships weight 400 only, so every 500/600/
    // 700 here was a browser-synthesized faux bold — smeared strokes at 11-13px.
    // Inheriting the body font (Lato, real weights) is the actual fix.
    //
    // Three levels, three small steps — section semibold and muted, group
    // medium and a shade darker, leaf regular and a shade quieter again. The
    // colours are deliberately close: a hard black-parent / pale-child split
    // makes the rail look like two different menus. Depth, weight and the
    // expand arrows do the work; colour only confirms it.
    //
    // The active row is marked by its background and accent rule alone. Its
    // label keeps the ordinary leaf colour: the tint already says "you are
    // here", and re-colouring the text says it a second time.
    const labelSx = isRoot
        ? {
              textTransform: 'uppercase',
              fontSize: '10.5px',
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: 'var(--nav-section-text, #5c6674)'
          }
        : hasChildren
        ? { fontSize: '13px', fontWeight: 500, color: 'var(--nav-parent-text, #3e4653)' }
        : { fontSize: '13px', fontWeight: 400, color: 'var(--nav-leaf-text, #5a6371)' };

    const linkProps = isLeafLink
        ? ({ component: Link, to: item.path } as any)
        : {};

    const listItem = (
        <ListItem
            button
            disableGutters
            onClick={handleClick}
            sx={listItemSx}
            {...linkProps}
        >
            {isRoot && item.icon && (
                <item.icon
                    sx={{
                        fontSize: 15,
                        // Undo the global MuiSvgIcon width: 0.7em (set without a
                        // height), which otherwise squashes the glyph.
                        width: '1em',
                        color: 'text.secondary',
                        mr: '8px',
                        flexShrink: 0
                    }}
                />
            )}
            <ListItemText
                primary={item.label}
                primaryTypographyProps={{ sx: labelSx } as any}
            />
            {hasChildren && (
                <ExpandMoreIcon
                    sx={{
                        fontSize: 16,
                        // The global MuiSvgIcon override sets width: 0.7em with
                        // no matching height, which squashes every icon. Undo it
                        // here rather than change it globally (200+ screens).
                        width: '1em',
                        color: 'text.secondary',
                        transition: 'transform 0.2s',
                        transform: expanded ? 'rotate(180deg)' : 'none',
                        mr: '4px'
                    }}
                />
            )}
        </ListItem>
    );

    const wrappedItem =
        !hasChildren && item.label ? (
            <Tooltip
                title={item.label}
                placement="right-start"
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: 'primary.main',
                            p: 1,
                            opacity: '0.85 !important',
                            fontSize: '12px',
                            lineHeight: 1
                        }
                    }
                }}
            >
                {listItem}
            </Tooltip>
        ) : (
            listItem
        );

    return (
        <>
            {wrappedItem}
            {hasChildren && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {children}
                    </List>
                </Collapse>
            )}
        </>
    );
});

SidebarMenuItem.displayName = 'SidebarMenuItem';

export default SidebarMenuItem;
